<script setup lang="ts">
const testimonials = [
  {
    name: 'Emma De Vries',
    role: 'Ouder van 2 kinderen',
    quote: 'Onze kinderen hadden het fantastisch! De begeleiders waren super betrokken en de activiteiten waren perfect afgestemd op hun leeftijd. Ze komen elk jaar weer terug en tellen nu al af tot het volgende kamp!',
    rating: 5,
    avatar: 'bx-user-circle',
    color: '#F27063',
  },
  {
    name: 'Thomas Janssens',
    role: 'Vader van Lucas (8 jaar)',
    quote: 'Lucas is nogal verlegen, maar bij \'t SpeelFabrik voelde hij zich meteen thuis. De kleine groepen en persoonlijke aandacht hebben echt het verschil gemaakt. Hij kwam thuis vol verhalen en met een grote glimlach!',
    rating: 5,
    avatar: 'bx-user-circle',
    color: '#8FB5D9',
  },
  {
    name: 'Sophie Claes',
    role: 'Mama van Nina en Finn',
    quote: 'Als werkende mama is het belangrijk dat ik mijn kinderen met een gerust hart kan achterlaten. Bij \'t SpeelFabrik weet ik dat ze veilig zijn, plezier maken en nieuwe dingen leren. Echt een aanrader!',
    rating: 5,
    avatar: 'bx-user-circle',
    color: '#FFD93D',
  },
]
</script>

<template>
  <div class="testimonials-section">
    <VContainer>
      <div class="section-header">
        <div class="section-badge">
          <VIcon icon="bx-message-square-dots" />
          <span>Wat Ouders Zeggen</span>
        </div>
        <h2 class="section-title">
          Ervaringen Van Tevreden Ouders
        </h2>
        <p class="section-description">
          Lees wat andere ouders vertellen over hun ervaring met 't SpeelFabrik en waarom hun kinderen elk jaar weer terugkomen.
        </p>
      </div>

      <div class="testimonials-grid">
        <div
          v-for="(testimonial, index) in testimonials"
          :key="index"
          class="testimonial-card"
          :style="{ '--card-delay': `${index * 0.15}s` }"
        >
          <div class="quote-icon">
            <VIcon
              icon="bxs-quote-alt-left"
              size="48"
              :style="{ color: testimonial.color }"
            />
          </div>

          <div class="rating">
            <VIcon
              v-for="star in testimonial.rating"
              :key="star"
              icon="bxs-star"
              size="20"
              color="warning"
            />
          </div>

          <p class="testimonial-quote">
            "{{ testimonial.quote }}"
          </p>

          <div class="testimonial-author">
            <div
              class="author-avatar"
              :style="{ background: `${testimonial.color}20` }"
            >
              <VIcon
                :icon="testimonial.avatar"
                size="40"
                :style="{ color: testimonial.color }"
              />
            </div>
            <div class="author-info">
              <div class="author-name">
                {{ testimonial.name }}
              </div>
              <div class="author-role">
                {{ testimonial.role }}
              </div>
            </div>
          </div>

          <div
            class="card-accent"
            :style="{ background: testimonial.color }"
          />
        </div>
      </div>

      <div class="section-footer">
        <VBtn
          color="primary"
          size="x-large"
          variant="flat"
        >
          Bekijk Meer Reviews
          <VIcon
            icon="bx-right-arrow-alt"
            end
          />
        </VBtn>
      </div>
    </VContainer>

    <!-- Background decorations -->
    <div class="bg-shapes">
      <div class="shape shape-1" />
      <div class="shape shape-2" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.testimonials-section {
  position: relative;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
  padding-block: 6rem;
}

.section-header {
  position: relative;
  z-index: 1;
  margin-block-end: 4rem;
  text-align: center;

  .section-badge {
    display: inline-flex;
    align-items: center;
    border-radius: 8px;
    background: rgba(242, 112, 99, 10%);
    color: #f27063;
    font-family: "Peachy Kee JF", "Public Sans", sans-serif;
    font-size: 0.9rem;
    font-weight: 600;
    gap: 0.5rem;
    letter-spacing: 0.5px;
    margin-block-end: 1.5rem;
    padding-block: 0.5rem;
    padding-inline: 1.5rem;
    text-transform: uppercase;

    .v-icon {
      color: currentcolor;
    }
  }

  .section-title {
    margin: 0;
    color: rgb(var(--v-theme-on-surface));
    font-family: "Peachy Kee JF", "Public Sans", sans-serif;
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 800;
    line-height: 1.2;
    margin-block-end: 1rem;
  }

  .section-description {
    color: #546e7a;
    font-size: 1.125rem;
    line-height: 1.6;
    margin-inline: auto;
    max-inline-size: 600px;
  }
}

.testimonials-grid {
  position: relative;
  z-index: 1;
  display: grid;
  gap: 2rem;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  margin-block-end: 3rem;
}

.quote-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(242, 112, 99, 5%);
  block-size: 80px;
  inline-size: 80px;
  margin-block-end: 1.5rem;
  transition: transform 0.3s ease;
}

.testimonial-card {
  position: relative;
  padding: 2.5rem;
  border-radius: 24px;
  animation: fade-in-up 0.6s ease-out forwards;
  animation-delay: var(--card-delay);
  background: white;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 6%);
  opacity: 0;
  transition: all 0.4s ease;

  &:hover {
    box-shadow: 0 12px 40px rgba(0, 0, 0, 12%);
    transform: translateY(-8px);

    .quote-icon {
      transform: scale(1.1) rotate(-5deg);
    }
  }
}

.rating {
  display: flex;
  gap: 0.25rem;
  margin-block-end: 1.5rem;
}

.testimonial-quote {
  position: relative;
  border-inline-start: 3px solid rgba(242, 112, 99, 20%);
  color: #2c3e50;
  font-size: 1.125rem;
  font-style: italic;
  line-height: 1.8;
  margin-block-end: 2rem;
  padding-inline-start: 1rem;
}

.testimonial-author {
  display: flex;
  align-items: center;
  gap: 1rem;

  .author-avatar {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    block-size: 60px;
    inline-size: 60px;
  }

  .author-info {
    flex: 1;

    .author-name {
      color: #2c3e50;
      font-size: 1.125rem;
      font-weight: 700;
      margin-block-end: 0.25rem;
    }

    .author-role {
      color: #546e7a;
      font-size: 0.875rem;
    }
  }
}

.card-accent {
  position: absolute;
  block-size: 4px;
  border-end-end-radius: 24px;
  border-end-start-radius: 24px;
  inline-size: 100%;
  inset-block-end: 0;
  inset-inline-start: 0;
}

.section-footer {
  position: relative;
  z-index: 1;
  margin-block-start: 2rem;
  text-align: center;
}

.bg-shapes {
  position: absolute;
  overflow: hidden;
  inset: 0;
  pointer-events: none;

  .shape {
    position: absolute;
    border-radius: 50%;
    opacity: 0.05;

    &.shape-1 {
      background: #f27063;
      block-size: 500px;
      inline-size: 500px;
      inset-block-start: -200px;
      inset-inline-end: -200px;
    }

    &.shape-2 {
      background: #8fb5d9;
      block-size: 400px;
      inline-size: 400px;
      inset-block-end: -150px;
      inset-inline-start: -150px;
    }
  }
}

@keyframes fade-in-up {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 960px) {
  .testimonials-section {
    padding-block: 4rem;
  }

  .testimonials-grid {
    gap: 1.5rem;
    grid-template-columns: 1fr;
  }

  .testimonial-card {
    padding: 2rem;
  }
}
</style>
